import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { TinyCard, CardHoverPreview } from "@/components/Card3D";
import { SelectedCardBar } from "@/components/SelectedCardBar";
import { traitColor, isMintTrait, ELEMENT_META, ResistGrid } from "@/components/ArenaBits";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { FlaskConical, Wand2, Dices, Dna, Sparkles, Coins, Search, Zap, ChevronLeft, ChevronRight, Wind, Heart, Swords, Shield, Check, ArrowUp, ArrowDown, ArrowRight, Lock, Star, Wrench } from "lucide-react";

const ABILITY_LABEL = { mend: "Mend", drain: "Drain", pierce: "Pierce", heavy_strike: "Heavy Strike" };
const RARITIES = ["all", "common", "uncommon", "rare", "epic", "legendary", "goddess"];
const SORTS = { spd: "Speed", hp: "Health", atk: "Attack", def: "Defense", title: "Name" };
const PAGE = 12;

const KINDS = [
  { key: "stats", icon: Dices, color: "#22d3ee", title: "Reroll Stats", action: "Gamble a brand-new base ATK / DEF / SPD / HP spread for this card's rarity." },
  { key: "ability", icon: Zap, color: "#c084fc", title: "Reroll Ability", action: "Swap the special move (Mend / Drain / Pierce / Heavy Strike) for a different one." },
  { key: "traits", icon: Dna, color: "#34d399", title: "Reroll Traits", action: "Re-discover this card's Find-on-Mint traits." },
  { key: "attacks", icon: Swords, color: "#fbbf24", title: "Reroll Attacks", action: "Re-roll this card's attack moves." },
  { key: "resist", icon: Shield, color: "#fb7185", title: "Reroll Elemental Resist", action: "Redistribute this card's elemental resistances onto a fresh set of elements (keeps the rarity's total strength)." },
];

// Compact text of a card's non-zero elemental resistances (from the resist view list).
function resistText(a) {
  const rs = (a?.resist || []).filter((r) => r.pct);
  if (!rs.length) return "none";
  return rs.map((r) => `${r.name} ${r.pct > 0 ? "+" : ""}${r.pct}%`).join(", ");
}

// What the card currently has for a given reroll kind (shown in the tooltip).
function currentOf(kind, a) {
  if (!a) return "—";
  if (kind === "stats") return `ATK ${a.atk} · DEF ${a.def} · SPD ${a.spd} · HP ${a.hp}`;
  if (kind === "ability") return ABILITY_LABEL[a.ability] || a.ability || "—";
  if (kind === "traits") return (a.attributes || []).map((x) => x.name).join(", ") || "None yet";
  if (kind === "attacks") return (a.moves || []).map((m) => m.name).join(", ") || "Strike";
  if (kind === "resist") return resistText(a);
  return "—";
}

// The card's individual items for a given reroll kind, used for the keep/reroll picker.
function itemsOf(kind, a) {
  if (!a) return [];
  if (kind === "stats") return [["atk", "ATK"], ["def", "DEF"], ["spd", "SPD"], ["hp", "HP"]].map(([k, l]) => ({ id: k, label: `${l} ${a[k] ?? "—"}` }));
  if (kind === "ability") return [{ id: "ability", label: ABILITY_LABEL[a.ability] || a.ability || "—" }];
  if (kind === "traits") return (a.attributes || []).map((x, i) => ({ id: String(i), label: x.name, locked: !!(x.locked || x.mint || x.name === "Cavern Mint"), trait: x }));
  if (kind === "attacks") return (a.moves || []).map((m, i) => ({ id: String(i), label: m.name, locked: m.id === "strike" || m.name === "Strike" }));
  return [];
}

function StatPip({ icon: Icon, label, value, color }) {
  return <span className="inline-flex items-center gap-1 font-mono text-[10px]" style={{ color }}><Icon className="h-3 w-3" /> {label} {value}</span>;
}

const STAT_DEFS = [
  { key: "atk", label: "ATK" },
  { key: "def", label: "DEF" },
  { key: "spd", label: "SPD" },
  { key: "hp", label: "HP" },
];

// Stat reroll result with green/red arrows showing each stat rose or fell.
function StatDiff({ before, after }) {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 font-mono text-[12px]" data-testid="forge-stat-diff">
      {STAT_DEFS.map(({ key, label }) => {
        const b = before[key];
        const a = after[key];
        const up = a > b;
        const down = a < b;
        const color = up ? "#34d399" : down ? "#f43f5e" : "rgba(255,255,255,0.55)";
        const Arrow = up ? ArrowUp : down ? ArrowDown : ArrowRight;
        return (
          <span key={key} className="inline-flex items-center gap-1" data-testid={`forge-stat-diff-${key}`}>
            <span className="text-white/45">{label}</span>
            <span className="text-white/55">{b}</span>
            <Arrow className="h-3 w-3" style={{ color }} />
            <span className="font-semibold" style={{ color }}>{a}</span>
          </span>
        );
      })}
    </div>
  );
}

function diffText(kind, before, after) {
  if (kind === "ability") return `${ABILITY_LABEL[before.ability] || before.ability} → ${ABILITY_LABEL[after.ability] || after.ability}`;
  if (kind === "stats") return `ATK ${before.atk}→${after.atk} · DEF ${before.def}→${after.def} · SPD ${before.spd}→${after.spd} · HP ${before.hp}→${after.hp}`;
  if (kind === "attacks") return `${(before.moves || []).map((m) => m.name).join(", ") || "—"}  →  ${(after.moves || []).map((m) => m.name).join(", ") || "—"}`;
  if (kind === "resist") return `${resistText(before)}  →  ${resistText(after)}`;
  return `${before.attributes.map((a) => a.name).join(", ") || "—"}  →  ${after.attributes.map((a) => a.name).join(", ") || "—"}`;
}

// Multi-edition chooser — each edition has its own rolled stats, so the player picks the exact card to forge.
function ForgeEditionPicker({ group, selected, onPick, onClose }) {
  if (!group) return null;
  const sorted = [...group.cards].sort((a, b) => (a.edition_number || 0) - (b.edition_number || 0));
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[hsl(222_22%_8%)] border-white/10 max-w-md max-h-[85vh] overflow-y-auto" data-testid="forge-edition-picker">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-white tracking-[0.06em]">{group.rep.title}</DialogTitle>
        </DialogHeader>
        <p className="text-[11px] text-white/50 -mt-1 mb-1">{sorted.length} editions owned — each has its own rolled stats. Pick the one to forge.</p>
        <div className="space-y-2">
          {sorted.map((c) => {
            const a = c.arena || {};
            const isSel = c.id === selected;
            return (
              <CardHoverPreview key={c.id} card={c} side="right">
                <div role="button" tabIndex={0} data-testid={`forge-edition-${c.id}`}
                  onClick={() => onPick(c.id)}
                  onKeyDown={(e) => { if (e.key === "Enter") onPick(c.id); }}
                  className={`hud-pill rounded-xl p-3 flex items-center justify-between gap-3 cursor-pointer transition-colors duration-150 outline-none ${isSel ? "border-fuchsia-300 bg-fuchsia-300/10" : "hover:bg-white/5 hover:border-fuchsia-400/30"}`}>
                  <div className="min-w-0">
                    <div className="font-mono text-sm text-white">Edition #{c.edition_number ?? "—"}</div>
                    <div className="flex flex-wrap gap-x-2.5 gap-y-0.5 mt-1">
                      <StatPip icon={Wind} label="SPD" value={a.spd ?? "—"} color="#22d3ee" />
                      <StatPip icon={Heart} label="HP" value={a.hp ?? "—"} color="#f43f5e" />
                      <StatPip icon={Swords} label="ATK" value={a.atk ?? "—"} color="#fbbf24" />
                      <StatPip icon={Shield} label="DEF" value={a.def ?? "—"} color="#a3e635" />
                    </div>
                  </div>
                  <Button size="sm" className={isSel ? "bg-emerald-400 text-black hover:bg-emerald-300 shrink-0" : "bg-fuchsia-400 text-black hover:bg-fuchsia-300 shrink-0"}>
                    {isSel ? <><Check className="h-3.5 w-3.5 mr-1" /> Selected</> : "Forge this"}
                  </Button>
                </div>
              </CardHoverPreview>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RerollBtn({ kindKey, color, cost, disabled, pending, onClick, token }) {
  return (
    <button
      type="button"
      data-testid={token ? `forge-reroll-token-${kindKey}` : `forge-reroll-${kindKey}`}
      disabled={disabled}
      onClick={onClick}
      title={token ? "Reroll using 1 Reroll Token (free)" : "Reroll with credits"}
      className="shrink-0 inline-flex items-center gap-1 h-7 px-2.5 rounded-md border text-[10px] font-mono whitespace-nowrap transition-opacity hover:opacity-90 disabled:cursor-not-allowed"
      style={{
        borderColor: disabled ? "rgba(255,255,255,0.12)" : `${color}99`,
        color: disabled ? "rgba(255,255,255,0.4)" : color,
        background: disabled ? "transparent" : `${color}1a`,
      }}
    >
      {token ? <Dices className="h-3 w-3" /> : <Wand2 className="h-3 w-3" />} {pending ? "…" : "Reroll"}
      {token
        ? <span className="inline-flex items-center gap-0.5 opacity-80"><Dices className="h-2.5 w-2.5" />1</span>
        : <span className="inline-flex items-center gap-0.5 opacity-70"><Coins className="h-2.5 w-2.5" />{cost.toLocaleString()}</span>}
    </button>
  );
}

function SectionHead({ title, color, kindKey, cost, disabled, pending, onReroll, tokens, tokenDisabled, onRerollToken }) {
  return (
    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
      <div className="font-mono text-[10px] uppercase tracking-[0.35em]" style={{ color: `${color}cc` }}>{title}</div>
      <RerollBtn kindKey={kindKey} color={color} cost={cost} disabled={disabled} pending={pending} onClick={onReroll} />
      {tokens > 0 && (
        <RerollBtn kindKey={kindKey} color="#c084fc" cost={0} disabled={tokenDisabled} pending={pending} onClick={onRerollToken} token />
      )}
    </div>
  );
}

// Tap-to-keep chip (stats / ability). Kept items are struck-through & dimmed and
// survive the reroll; highlighted chips get rerolled.
function KeepChip({ kindKey, item, kept, color, onToggle }) {
  return (
    <button
      type="button"
      data-testid={`forge-keep-${kindKey}-${item.id}`}
      onClick={() => onToggle(kindKey, item.id)}
      title={kept ? "Kept — won't be rerolled" : "Will be rerolled — tap to keep"}
      className={`font-mono text-[11px] px-2 py-1 rounded-md border transition-all flex items-center gap-1 ${kept ? "border-white/10 bg-black/40 text-white/30 line-through opacity-60" : "text-white"}`}
      style={kept ? {} : { borderColor: `${color}99`, background: `${color}1f` }}>
      {kept && <Check className="h-3 w-3 shrink-0" />}{item.label}
    </button>
  );
}

// Interactive in-profile forge view: each section header carries its own Reroll
// button and every shown item can be tapped to keep/unkeep it (same rules as the
// old grid). Mint-locked traits can never be rerolled.
function ForgeProfile({ arena, keep, toggleKeep, reroll, costs, credits, tokens }) {
  if (!arena) return null;
  const pending = reroll.isPending;
  const colorOf = (k) => (KINDS.find((x) => x.key === k) || {}).color || "#22d3ee";
  const meta = (kind) => {
    const items = itemsOf(kind, arena);
    const keptSet = keep[kind] || new Set();
    const allKept = items.length > 0 && items.every((it) => it.locked || keptSet.has(it.id));
    const creditDisabled = pending || credits < (costs[kind] || 0) || allKept;
    const tokenDisabled = pending || tokens < 1 || allKept;
    return { items, keptSet, creditDisabled, tokenDisabled, color: colorOf(kind), cost: costs[kind] || 0 };
  };
  const fire = (kind, keptSet, useToken) => reroll.mutate({ kind, keepIds: [...keptSet], useToken });
  const stats = meta("stats");
  const ability = meta("ability");
  const traits = meta("traits");
  const attacks = meta("attacks");
  const resist = meta("resist");

  return (
    <div className="space-y-3" data-testid="arena-profile">
      {/* STATS */}
      <div>
        <SectionHead title="Stats" color={stats.color} kindKey="stats" cost={stats.cost} disabled={stats.creditDisabled} pending={pending} onReroll={() => fire("stats", stats.keptSet, false)} tokens={tokens} tokenDisabled={stats.tokenDisabled} onRerollToken={() => fire("stats", stats.keptSet, true)} />
        <div className="flex flex-wrap gap-1.5">
          {stats.items.map((it) => (
            <KeepChip key={it.id} kindKey="stats" item={it} kept={stats.keptSet.has(it.id)} color={stats.color} onToggle={toggleKeep} />
          ))}
        </div>
      </div>

      {/* ABILITY */}
      <div>
        <SectionHead title="Ability" color={ability.color} kindKey="ability" cost={ability.cost} disabled={ability.creditDisabled} pending={pending} onReroll={() => fire("ability", ability.keptSet, false)} tokens={tokens} tokenDisabled={ability.tokenDisabled} onRerollToken={() => fire("ability", ability.keptSet, true)} />
        <div className="flex flex-wrap gap-1.5">
          {ability.items.map((it) => (
            <KeepChip key={it.id} kindKey="ability" item={it} kept={ability.keptSet.has(it.id)} color={ability.color} onToggle={toggleKeep} />
          ))}
        </div>
      </div>

      {/* TRAITS */}
      {arena.attributes?.length > 0 && (
        <div>
          <SectionHead title="Traits" color={traits.color} kindKey="traits" cost={traits.cost} disabled={traits.creditDisabled} pending={pending} onReroll={() => fire("traits", traits.keptSet, false)} tokens={tokens} tokenDisabled={traits.tokenDisabled} onRerollToken={() => fire("traits", traits.keptSet, true)} />
          <div className="space-y-1.5">
            {arena.attributes.map((a, i) => {
              const id = String(i);
              const locked = isMintTrait(a);
              const kept = traits.keptSet.has(id);
              const tc = traitColor(a);
              const col = tc.color || "#cbd5e1";
              return (
                <button key={id} type="button" disabled={locked}
                  data-testid={locked ? `forge-locked-traits-${id}` : `forge-keep-traits-${id}`}
                  onClick={() => !locked && toggleKeep("traits", id)}
                  title={locked ? "Mint-locked trait — permanent, can never be rerolled" : kept ? "Kept — won't be rerolled" : "Will be rerolled — tap to keep"}
                  className={`w-full text-left relative overflow-hidden rounded-lg border px-2.5 py-1.5 transition-all ${locked ? "cursor-not-allowed" : "cursor-pointer hover:brightness-125"} ${kept || locked ? "opacity-55" : ""}`}
                  style={{ borderColor: `${col}40`, background: `linear-gradient(90deg, ${col}1f, ${col}08 45%, transparent)` }}>
                  <span className="absolute left-0 top-0 h-full w-[3px]" style={{ background: col, boxShadow: `0 0 10px ${col}` }} />
                  <div className="flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-md shrink-0" style={{ background: `${col}22`, border: `1px solid ${col}55` }}>
                      {locked ? <Lock className="h-3 w-3" style={{ color: col }} /> : kept ? <Check className="h-3 w-3" style={{ color: col }} /> : <Star className="h-3 w-3" style={{ color: col }} />}
                    </span>
                    <span className={`font-mono font-semibold text-[13px] leading-none ${kept ? "line-through" : ""}`} style={tc}>{a.name}</span>
                    {locked && <span className="ml-auto text-[8px] uppercase tracking-wider text-amber-200 px-1.5 py-px rounded bg-amber-400/10 border border-amber-300/40">mint</span>}
                  </div>
                  {a.desc && <div className="text-white/60 text-[11px] leading-snug mt-1 pl-7">{a.desc}</div>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ATTACKS */}
      {arena.moves?.length > 0 && (
        <div>
          <SectionHead title="Attacks" color={attacks.color} kindKey="attacks" cost={attacks.cost} disabled={attacks.creditDisabled} pending={pending} onReroll={() => fire("attacks", attacks.keptSet, false)} tokens={tokens} tokenDisabled={attacks.tokenDisabled} onRerollToken={() => fire("attacks", attacks.keptSet, true)} />
          <div className="space-y-1.5">
            {arena.moves.map((m, i) => {
              const id = String(i);
              const locked = m.id === "strike" || m.name === "Strike";
              const kept = attacks.keptSet.has(id);
              const el = ELEMENT_META[m.element || "kinetic"] || ELEMENT_META.kinetic;
              const Icon = el.Icon;
              const col = el.color;
              return (
                <button key={m.id || i} type="button" disabled={locked}
                  data-testid={locked ? `forge-locked-attacks-${id}` : `forge-keep-attacks-${id}`}
                  onClick={() => !locked && toggleKeep("attacks", id)}
                  title={locked ? "Basic attack — every fighter knows Strike, it can never be rerolled" : kept ? "Kept — won't be rerolled" : "Will be rerolled — tap to keep"}
                  className={`w-full text-left relative overflow-hidden rounded-lg border px-2.5 py-1.5 transition-all ${locked ? "cursor-not-allowed" : "cursor-pointer hover:brightness-125"} ${kept || locked ? "opacity-55" : ""}`}
                  style={{ borderColor: `${col}40`, background: `linear-gradient(90deg, ${col}1f, ${col}08 45%, transparent)` }}>
                  <span className="absolute left-0 top-0 h-full w-[3px]" style={{ background: col, boxShadow: `0 0 10px ${col}` }} />
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="flex h-5 w-5 items-center justify-center rounded-md shrink-0" style={{ background: `${col}22`, border: `1px solid ${col}55` }}>
                      {locked ? <Lock className="h-3 w-3" style={{ color: col }} /> : kept ? <Check className="h-3 w-3" style={{ color: col }} /> : <Icon className="h-3 w-3" style={{ color: col }} />}
                    </span>
                    <span className={`font-mono font-semibold text-[13px] leading-none ${kept ? "line-through" : ""}`} style={{ color: col }}>{m.name}</span>
                    {locked && <span className="ml-auto rounded px-1.5 py-px text-[8px] uppercase tracking-wider text-white/55 bg-white/5 border border-white/15">basic</span>}
                    {!locked && <span className="ml-auto rounded px-1.5 py-px text-[8px] uppercase tracking-wider" style={{ color: col, background: `${col}22`, border: `1px solid ${col}55` }}>{el.name}</span>}
                  </div>
                  {m.desc && <div className="text-white/60 text-[11px] leading-snug mt-1 pl-7 break-words">{m.desc}</div>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* GEAR (read-only) */}
      {arena.gear?.length > 0 && (
        <div data-testid="arena-gear-bonuses">
          <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-amber-300/80 mb-1.5">From Gear &amp; Items</div>
          <div className="space-y-1.5">
            {arena.gear.map((g, i) => (
              <div key={g.id || `${g.name}-${i}`} className="text-[13px] leading-tight">
                <span className="font-mono font-semibold inline-flex items-center gap-1.5 text-amber-200">
                  <Wrench className="h-3.5 w-3.5 shrink-0" /> {g.name}
                  {g.slot && <span className="text-[8px] uppercase tracking-wider opacity-70">· {g.slot}</span>}
                </span>
                {g.desc && g.desc !== "—" && <div className="text-white/60 text-[11px] leading-snug mt-0.5 break-words">{g.desc}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ELEMENTAL RESIST */}
      {arena.resist?.length > 0 && (
        <div>
          <SectionHead title="Elemental Resist" color={resist.color} kindKey="resist" cost={resist.cost} disabled={resist.creditDisabled} pending={pending} onReroll={() => fire("resist", new Set(), false)} tokens={tokens} tokenDisabled={resist.tokenDisabled} onRerollToken={() => fire("resist", new Set(), true)} />
          <ResistGrid resist={arena.resist} hideLabel />
          <div className="text-[9px] text-white/35 mt-1.5">Reroll redistributes your resistances onto a fresh set of elements — same total strength, new coverage.</div>
        </div>
      )}
    </div>
  );
}

function ForgePanel({ cardId }) {
  const qc = useQueryClient();
  const { user, refresh } = useAuth();
  const [result, setResult] = useState(null);
  const [keep, setKeep] = useState({ stats: new Set(), ability: new Set(), traits: new Set(), attacks: new Set(), resist: new Set() });
  const toggleKeep = (kind, id) => {
    const s = new Set(keep[kind]);
    s.has(id) ? s.delete(id) : s.add(id);
    setKeep((prev) => ({ ...prev, [kind]: s }));
    api.post(`/cards/${cardId}/forge/keep`, { kind, keep: Array.from(s) }).catch(() => {});
  };

  const { data: q } = useQuery({
    queryKey: ["forge", cardId],
    queryFn: async () => (await api.get(`/cards/${cardId}/forge`)).data,
  });

  // Load the card's saved keep selection so strike-throughs persist across rerolls/reloads.
  useEffect(() => {
    if (!q?.keep) return;
    setKeep({
      stats: new Set(q.keep.stats || []),
      ability: new Set(q.keep.ability || []),
      traits: new Set(q.keep.traits || []),
      attacks: new Set(q.keep.attacks || []),
      resist: new Set(q.keep.resist || []),
    });
  }, [q?.keep]);

  const reroll = useMutation({
    mutationFn: async ({ kind, keepIds, useToken }) => (await api.post(`/cards/${cardId}/forge`, { kind, keep: keepIds, use_token: !!useToken })).data,
    onSuccess: (d) => {
      setResult({ kind: d.kind, before: d.before, after: d.after, text: diffText(d.kind, d.before, d.after) });
      qc.invalidateQueries({ queryKey: ["forge", cardId] });
      qc.invalidateQueries({ queryKey: ["card", cardId] });
      qc.invalidateQueries({ queryKey: ["my-cards"] });
      refresh();
      toast.success("The forge reshaped your card!");
    },
    onError: (e) => toast.error(apiError(e, "Reroll failed")),
  });

  if (!q) return <div className="text-white/40 py-16 text-center">Heating the forge…</div>;
  const credits = user?.credits ?? 0;
  const tokens = q.reroll_tokens ?? 0;

  return (
    <div data-testid="forge-panel">
      <div className="hud-pill rounded-2xl p-4">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">Current profile · {q.title}</div>
          <div className="flex items-center gap-3">
            {tokens > 0 && (
              <div className="font-mono text-[10px] text-fuchsia-300 flex items-center gap-1" data-testid="forge-token-balance"><Dices className="h-3 w-3" /> {tokens} token{tokens === 1 ? "" : "s"}</div>
            )}
            <div className="font-mono text-[10px] text-white/40 flex items-center gap-1"><Coins className="h-3 w-3" /> {credits.toLocaleString()}</div>
          </div>
        </div>
        <ForgeProfile arena={q.arena} keep={keep} toggleKeep={toggleKeep} reroll={reroll} costs={q.costs} credits={credits} tokens={tokens} />
        <p className="text-[11px] text-white/35 mt-3 flex items-center gap-1.5">
          <Wand2 className="h-3.5 w-3.5 shrink-0" /> Tap any item to KEEP it (struck-through) — highlighted items get rerolled. {tokens > 0 ? "Use the fuchsia Reroll button to spend 1 Reroll Token instead of credits." : "Every reroll is a gamble; only this edition changes."}
        </p>
      </div>

      <AnimatePresence>
        {result && (
          <motion.div key={result.text} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="mt-4 rounded-xl border border-fuchsia-400/40 bg-fuchsia-500/[0.08] p-3" data-testid="forge-result">
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-fuchsia-300 mb-1 flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> {result.kind} rerolled</div>
            {result.kind === "stats" && result.before ? (
              <StatDiff before={result.before} after={result.after} />
            ) : (
              <div className="text-[12px] text-white/80 break-words">{result.text}</div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function WaifuForgePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState(null);
  const [editionPick, setEditionPick] = useState(null);
  const [search, setSearch] = useState("");
  const [rarity, setRarity] = useState("all");
  const [sort, setSort] = useState("spd");
  const [page, setPage] = useState(0);

  const { data: cards } = useQuery({ queryKey: ["my-cards"], queryFn: async () => (await api.get("/cards/mine")).data });

  // Group duplicate editions of the same template into one stacked tile (overlap + count badge + hover preview).
  const groups = useMemo(() => {
    let list = [...(cards || [])];
    list = list.filter((c) => !c.in_use); // hide cards committed elsewhere (trade/market/battle/show/request/commission/cavern)
    if (search.trim()) list = list.filter((c) => (c.title || "").toLowerCase().includes(search.toLowerCase()));
    if (rarity !== "all") list = list.filter((c) => c.rarity === rarity);
    const map = new Map();
    for (const c of list) {
      const key = c.template_id || c.id;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    }
    const gs = [...map.values()].map((cs) => ({ key: cs[0].template_id || cs[0].id, cards: cs, rep: cs[0] }));
    gs.sort((a, b) => sort === "title" ? (a.rep.title || "").localeCompare(b.rep.title || "") : ((b.rep.arena?.[sort] ?? 0) - (a.rep.arena?.[sort] ?? 0)));
    return gs;
  }, [cards, search, rarity, sort]);

  const pageCount = Math.max(1, Math.ceil(groups.length / PAGE));
  const safePage = Math.min(page, pageCount - 1);
  const shown = groups.slice(safePage * PAGE, safePage * PAGE + PAGE);

  const pickGroup = (g) => {
    if (g.cards.length > 1) setEditionPick(g);
    else setSelected(g.rep.id);
  };

  useEffect(() => {
    const pre = searchParams.get("preselect");
    if (pre && cards && cards.some((c) => c.id === pre)) {
      setSelected(pre);
      toast.success("Card pre-selected from your collection");
      const next = new URLSearchParams(searchParams);
      next.delete("preselect");
      setSearchParams(next, { replace: true });
    }
  }, [cards, searchParams, setSearchParams]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="upgrade-page">
      <div className="flex items-center gap-3 mb-1">
        <div className="h-11 w-11 rounded-xl bg-fuchsia-500/15 border border-fuchsia-400/30 flex items-center justify-center"><FlaskConical className="h-6 w-6 text-fuchsia-300" /></div>
        <div>
          <h1 className="font-display text-4xl sm:text-5xl text-white tracking-[0.04em]">UPGRADE</h1>
          <p className="text-white/50 text-sm">Pay to reroll a card's <span className="text-cyan-300">stats</span>, <span className="text-fuchsia-300">ability</span> or <span className="text-emerald-300">traits</span>. Sometimes you climb, sometimes you fall.</p>
        </div>
      </div>

      <div className="mt-8">
        {/* Picker collapses on select; the forge then flows full-width beneath the hero card */}
        <div>
          {selected ? (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              <SelectedCardBar card={(cards || []).find((c) => c.id === selected)} onClear={() => setSelected(null)} testid="forge-selected" />
              <ForgePanel cardId={selected} key={selected} />
            </motion.div>
          ) : (
          <>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/45 mr-auto">Choose a card to forge</h2>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/30" />
              <Input data-testid="forge-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} placeholder="Search…" className="h-8 w-36 pl-7 bg-[hsl(220_18%_12%)] border-white/10 text-white text-xs" />
            </div>
            <Select value={rarity} onValueChange={(v) => { setRarity(v); setPage(0); }}>
              <SelectTrigger data-testid="forge-rarity-filter" className="h-8 w-28 bg-[hsl(220_18%_12%)] border-white/10 text-white text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[hsl(222_22%_8%)] border-white/10 text-white">{RARITIES.map((r) => <SelectItem key={r} value={r} className="text-xs capitalize">{r}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="forge-sort" className="h-8 w-28 bg-[hsl(220_18%_12%)] border-white/10 text-white text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[hsl(222_22%_8%)] border-white/10 text-white">{Object.entries(SORTS).map(([k, v]) => <SelectItem key={k} value={k} className="text-xs">{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {groups.length === 0 ? (
            <div className="hud-pill rounded-2xl p-10 text-center text-white/45">No cards match. Mint some from the Store first.</div>
          ) : (
            <>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-5 gap-3">
                {shown.map((g) => {
                  const sel = g.cards.find((c) => c.id === selected);
                  const statCard = sel || g.rep;
                  const a = statCard.arena || {};
                  const isSel = !!sel;
                  return (
                    <div key={g.key} data-testid={`forge-pick-${g.rep.id}`}
                      className={`rounded-xl transition-transform ${isSel ? "ring-2 ring-fuchsia-300 ring-offset-2 ring-offset-[hsl(220_22%_6%)] scale-[1.02]" : ""}`}>
                      <TinyCard
                        card={statCard}
                        count={g.cards.length}
                        idKey={g.key}
                        previewSide="top"
                        onClick={() => pickGroup(g)}
                        footer={
                          <div className="mt-1 px-0.5">
                            <div className="text-white text-[11px] font-medium truncate flex items-center gap-1">
                              {isSel && <Check className="h-3 w-3 text-fuchsia-300 shrink-0" />}{statCard.title}
                            </div>
                            <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
                              <StatPip icon={Wind} label="SPD" value={a.spd ?? "—"} color="#22d3ee" />
                              <StatPip icon={Heart} label="HP" value={a.hp ?? "—"} color="#f43f5e" />
                            </div>
                          </div>
                        }
                      />
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-between mt-3">
                <span className="font-mono text-[10px] text-white/35">{groups.length} card{groups.length === 1 ? "" : "s"} · page {safePage + 1}/{pageCount}</span>
                <div className="flex gap-2">
                  <Button data-testid="forge-prev-page" size="sm" variant="outline" disabled={safePage === 0} onClick={() => setPage(safePage - 1)} className="border-white/15 text-white/70 h-7"><ChevronLeft className="h-4 w-4" /></Button>
                  <Button data-testid="forge-next-page" size="sm" variant="outline" disabled={safePage >= pageCount - 1} onClick={() => setPage(safePage + 1)} className="border-white/15 text-white/70 h-7"><ChevronRight className="h-4 w-4" /></Button>
                </div>
              </div>
            </>
          )}
          </>
          )}
        </div>
      </div>

      <ForgeEditionPicker
        group={editionPick}
        selected={selected}
        onPick={(id) => { setSelected(id); setEditionPick(null); }}
        onClose={() => setEditionPick(null)}
      />
    </div>
  );
}
